/**
 * Shared formatting + ordering for the detection feed.
 *
 * Both surfaces that render alerts (Monitoring's full feed and the dashboard's
 * "Recent activity" panel) had the same two defects, independently: they
 * printed the raw ISO timestamp including milliseconds, and they rendered in
 * whatever order the API returned. The dashboard compounded it by taking
 * `slice(0, 6)` of an unordered list, so "recent activity" was six arbitrary
 * alerts rather than the six most recent.
 *
 * One implementation, so the two can no longer disagree about what "recent"
 * means.
 */

export type AlertSeverityName = 'info' | 'warning' | 'critical';

/** Worst first, for tie-breaking equal timestamps. */
export const SEVERITY_RANK: Record<AlertSeverityName, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/** `11:53:00` — the part an analyst actually reads. Falls back to the input. */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour12: false });
}

/** `4m ago` / `2h ago`. Empty string when the timestamp cannot be parsed. */
export function relativeAge(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

/**
 * Newest first, worst-severity first on a tie. Never drops a row: an
 * unparseable timestamp sorts to the end rather than disappearing.
 */
export function byRecencyThenSeverity<T extends { ts: string; severity: AlertSeverityName }>(
  items: ReadonlyArray<T>,
): T[] {
  return [...items].sort((a, b) => {
    const ta = new Date(a.ts).getTime();
    const tb = new Date(b.ts).getTime();
    const aBad = Number.isNaN(ta);
    const bBad = Number.isNaN(tb);
    if (aBad !== bBad) return aBad ? 1 : -1;
    if (!aBad && !bBad && ta !== tb) return tb - ta;
    return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  });
}
